<template>
  <div
    :class="displayClass"
    :style="{ display: displayStyle ? 'block' : 'none' }"
    v-on:click="onBackdrop($event)"
    style="z-index: 9998; background-color: rgba(0,0,0,.4);"
    class="modal fade" tabindex="-1" role="dialog"
  >
    <div class="modal-dialog modal-dialog-centered px-3" v-on:click="$event.stopPropagation()" role="document">
      <div class="modal-content bg-gray rounded-lg">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('Record a jingle') }}</h5>
          <span @click="closeModal" class="ml-auto clickable"><FontAwesomeIcon icon="times" class="text-danger" /></span>
        </div>

        <div class="modal-body px-4 py-4">
          <input
            v-model="title"
            type="text"
            :placeholder="$t('Jingle title')"
            class="form-control rounded border-primary mb-4"
            :disabled="state === 'recording' || uploading"
          >

          <div class="record-area d-flex flex-column align-items-center justify-content-center text-center">

            <!-- Microphone permission / recording error -->
            <div v-if="micError" class="w-100">
              <p class="text-danger mb-2">
                <FontAwesomeIcon icon="microphone" class="mr-2" />
                <span v-if="micError === 'denied'">{{ $t('Microphone access denied. Please allow microphone access in your browser settings.') }}</span>
                <span v-else-if="micError === 'insecure'">{{ $t('Recording requires a secure (HTTPS) connection.') }}</span>
                <span v-else>{{ $t('Recording failed') }}</span>
              </p>
              <button v-if="micError === 'failed'" class="btn btn-outline-primary" @click="startRecording">
                {{ $t('Try again') }}
              </button>
            </div>

            <!-- Idle: ready to record -->
            <template v-else-if="state === 'idle'">
              <button type="button" class="record-btn record-btn-idle" @click="startRecording">
                <FontAwesomeIcon icon="microphone" />
              </button>
              <p class="text-white mt-3 mb-1">{{ $t('Tap to record') }}</p>
              <small class="text-muted">{{ $t('Maximum 60 seconds') }}</small>
            </template>

            <!-- Recording in progress -->
            <template v-else-if="state === 'recording'">
              <button type="button" class="record-btn record-btn-recording" @click="stopRecording">
                <FontAwesomeIcon icon="stop" />
              </button>
              <p class="text-white mt-3 mb-1 record-timer">
                {{ formattedElapsed }} / {{ formattedMax }}
              </p>
              <small class="text-muted">{{ $t('Recording...') }}</small>
            </template>

            <!-- Preview recorded clip -->
            <template v-else-if="state === 'preview'">
              <div class="d-flex align-items-center justify-content-center w-100">
                <button type="button" class="preview-btn text-primary mr-3" @click="togglePreview">
                  <FontAwesomeIcon :icon="previewPlaying ? 'pause' : 'play'" />
                </button>
                <span class="text-white record-timer mr-3">{{ formattedRecorded }}</span>
                <button
                  type="button"
                  class="preview-btn text-danger"
                  :title="$t('Delete recording')"
                  @click="discardRecording"
                >
                  <FontAwesomeIcon icon="trash-alt" />
                </button>
              </div>
            </template>
          </div>
        </div>

        <div class="modal-footer d-flex">
          <button @click="closeModal" class="btn btn-link text-muted flex-fill" :disabled="uploading">
            {{ $t('Cancel') }}
          </button>
          <button
            @click="upload"
            class="btn btn-primary flex-fill"
            :disabled="!canUpload || uploading"
          >
            <FontAwesomeIcon v-if="uploading" icon="spinner" spin class="mr-2" />
            {{ $t('Upload') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import musicboxClient from '@/utils/musicboxClient'
import { secondsToMinAndSec } from '@/utils/numberUtils'
import { createRecorder } from '@/utils/audioRecorder'

const MAX_DURATION = 60

export default {

  name: 'JingleRecordModal',

  props: {
    displayModal: Boolean,
    onClose: Function
  },

  data () {
    return {
      displayClass: '',
      displayStyle: false,
      state: 'idle', // idle | recording | preview
      micError: null, // null | 'denied' | 'failed'
      title: '',
      elapsed: 0,
      timerId: null,
      recorder: null,
      recordedBlob: null,
      recordedDuration: 0,
      objectUrl: null,
      previewAudio: null,
      previewPlaying: false,
      uploading: false
    }
  },

  computed: {
    formattedElapsed () {
      return secondsToMinAndSec(Math.min(Math.floor(this.elapsed), MAX_DURATION))
    },
    formattedMax () {
      return secondsToMinAndSec(MAX_DURATION)
    },
    formattedRecorded () {
      return secondsToMinAndSec(Math.round(this.recordedDuration))
    },
    canUpload () {
      return this.state === 'preview' && this.recordedBlob !== null && this.title.trim() !== ''
    }
  },

  watch: {
    displayModal (newVal) {
      if (newVal === true) {
        this.displayStyle = true
        setTimeout(() => { this.displayClass = 'show' }, 100)
      } else {
        this.displayClass = ''
        setTimeout(() => { this.displayStyle = false }, 100)
      }
    }
  },

  beforeDestroy () {
    this.cleanup()
  },

  methods: {
    async startRecording () {
      this.micError = null
      const recorder = createRecorder()
      this.recorder = recorder
      try {
        await recorder.start()
      } catch (error) {
        this.recorder = null
        try { recorder.dispose() } catch (e) {}
        var name = error && error.name
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
          this.micError = 'denied'
        } else if (name === 'InsecureContextError') {
          this.micError = 'insecure'
        } else {
          this.micError = 'failed'
        }
        return
      }
      this.elapsed = 0
      this.state = 'recording'
      var startTime = Date.now()
      this.timerId = setInterval(() => {
        this.elapsed = (Date.now() - startTime) / 1000
        if (this.elapsed >= MAX_DURATION) {
          this.stopRecording()
        }
      }, 200)
    },

    async stopRecording () {
      if (this.state !== 'recording' || !this.recorder) { return }
      this.clearTimer()
      var recorder = this.recorder
      try {
        var result = await recorder.stop()
        this.recordedBlob = result.blob
        this.recordedDuration = result.duration || this.elapsed
        this.objectUrl = URL.createObjectURL(result.blob)
        this.state = 'preview'
      } catch (error) {
        this.micError = 'failed'
        this.state = 'idle'
      }
    },

    togglePreview () {
      if (!this.objectUrl) { return }
      if (this.previewPlaying && this.previewAudio) {
        this.previewAudio.pause()
        return
      }
      if (!this.previewAudio) {
        this.previewAudio = new Audio(this.objectUrl)
        this.previewAudio.onended = () => { this.previewPlaying = false }
        this.previewAudio.onpause = () => { this.previewPlaying = false }
        this.previewAudio.onplay = () => { this.previewPlaying = true }
      }
      this.previewAudio.play()
    },

    discardRecording () {
      this.stopPreview()
      this.revokeUrl()
      this.recordedBlob = null
      this.recordedDuration = 0
      if (this.recorder) {
        try { this.recorder.dispose() } catch (e) {}
        this.recorder = null
      }
      this.state = 'idle'
    },

    async upload () {
      if (!this.canUpload) { return }
      this.uploading = true
      try {
        const response = await musicboxClient.uploadJingle(this.recordedBlob, this.title.trim())
        this.$emit('uploaded', response.data.jingle_created)
        this.reset()
        if (this.onClose) { this.onClose() }
      } catch (error) {
        var msg = this.$t('Failed to upload jingle')
        if (error.response && error.response.data && error.response.data.detail) {
          msg = Array.isArray(error.response.data.detail) ? error.response.data.detail[0] : error.response.data.detail
        }
        this.$notify.danger(msg)
      } finally {
        this.uploading = false
      }
    },

    onBackdrop (e) {
      e.stopPropagation()
      this.closeModal()
    },

    closeModal () {
      if (this.uploading) { return }
      this.reset()
      if (this.onClose) { this.onClose() }
    },

    clearTimer () {
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
    },

    stopPreview () {
      if (this.previewAudio) {
        this.previewAudio.onended = null
        this.previewAudio.onpause = null
        this.previewAudio.onplay = null
        try { this.previewAudio.pause() } catch (e) {}
        this.previewAudio = null
      }
      this.previewPlaying = false
    },

    revokeUrl () {
      if (this.objectUrl) {
        try { URL.revokeObjectURL(this.objectUrl) } catch (e) {}
        this.objectUrl = null
      }
    },

    cleanup () {
      this.clearTimer()
      this.stopPreview()
      this.revokeUrl()
      if (this.recorder) {
        try { this.recorder.dispose() } catch (e) {}
        this.recorder = null
      }
    },

    reset () {
      this.cleanup()
      this.state = 'idle'
      this.micError = null
      this.title = ''
      this.elapsed = 0
      this.recordedBlob = null
      this.recordedDuration = 0
    }
  }
}
</script>

<style scoped>
.record-area {
  min-height: 150px;
}

.record-btn {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 3px solid;
  background-color: transparent;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: background-color 0.15s ease;
}

.record-btn-idle {
  border-color: #dc3545;
  color: #dc3545;
}

.record-btn-idle:active {
  background-color: rgba(220, 53, 69, 0.15);
}

.record-btn-recording {
  border-color: #dc3545;
  background-color: #dc3545;
  color: #fff;
  animation: record-pulse 1.4s infinite;
}

@keyframes record-pulse {
  0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.5); }
  70% { box-shadow: 0 0 0 14px rgba(220, 53, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
}

.record-timer {
  font-variant-numeric: tabular-nums;
  font-size: 20px;
}

.preview-btn {
  background: transparent;
  border: none;
  outline: none;
  font-size: 28px;
  padding: 0.25rem 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/components/JingleRecordModal.vue